import type { MetamorphopsiaState } from "@client/types/vision";
import { bilinearSample } from "./bilinearSample";
import { regionScaleFalloff } from "./regionScale";

/**
 * Apply metamorphopsia (wave distortion) to image data.
 *
 * Whole mode warps the entire image. F11 region mode (`mode === "region"`)
 * confines the wave to an ellipse and multiplies the amplitude by a bridge
 * falloff so the waviness fades to zero at the edge (no hard wave boundary).
 * Sampling is bilinear for smooth waves.
 *
 * @param imageData - ImageData to modify (mutated in place)
 * @param metamorphopsia - Metamorphopsia configuration
 * @returns the same ImageData
 */
export function applyMetamorphopsiaToImageData(
	imageData: ImageData,
	metamorphopsia: MetamorphopsiaState,
): ImageData {
	if (!metamorphopsia.enabled) return imageData;
	const { amplitude, frequency, direction } = metamorphopsia;
	if (amplitude === 0 || frequency === 0) return imageData;

	const data = imageData.data;
	const width = imageData.width;
	const height = imageData.height;
	const originalData = new Uint8ClampedArray(data);

	const horiz = direction === "horizontal" || direction === "both";
	const vert = direction === "vertical" || direction === "both";

	// Sample the wave-displaced source (bilinear) and write into `data`.
	const writePixel = (x: number, y: number, amp: number) => {
		let sourceX = x;
		let sourceY = y;
		if (horiz)
			sourceX = x + amp * Math.sin((y / height) * frequency * Math.PI * 2);
		if (vert)
			sourceY = y + amp * Math.sin((x / width) * frequency * Math.PI * 2);
		const [r, g, b, a] = bilinearSample(
			originalData,
			width,
			height,
			sourceX,
			sourceY,
		);
		const i = (y * width + x) * 4;
		data[i] = r;
		data[i + 1] = g;
		data[i + 2] = b;
		data[i + 3] = a;
	};

	// F11: region mode — only warp inside the ellipse, amplitude faded by the
	// bridge falloff (reused from F10's regionScale).
	const region =
		metamorphopsia.mode === "region" ? metamorphopsia.region : null;

	if (region) {
		const { cx, cy, rx, ry } = region;
		if (rx <= 0 || ry <= 0) return imageData;
		const bridge = metamorphopsia.bridge ?? 0.5;
		const bx0 = Math.max(0, Math.floor(cx - rx));
		const by0 = Math.max(0, Math.floor(cy - ry));
		const bx1 = Math.min(width - 1, Math.ceil(cx + rx));
		const by1 = Math.min(height - 1, Math.ceil(cy + ry));
		for (let y = by0; y <= by1; y++) {
			for (let x = bx0; x <= bx1; x++) {
				const nx = (x - cx) / rx;
				const ny = (y - cy) / ry;
				const t = regionScaleFalloff(Math.sqrt(nx * nx + ny * ny), bridge);
				if (t <= 0) continue; // rim/outside: leave the original pixel
				writePixel(x, y, amplitude * t);
			}
		}
		return imageData;
	}

	// Whole-image wave.
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			writePixel(x, y, amplitude);
		}
	}
	return imageData;
}
